import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { DpEmployeeListItem, listEmployeesApi } from "../api/employeesApi";
import { formatCpf } from "../utils/format";
import EmployeeStatusBadge from "./EmployeeStatusBadge";

type Props = {
  token: string;
  title: string;
  onClose: () => void;
  onPick: (employee: DpEmployeeListItem) => void;
};

/**
 * Escolha do colaborador antes de um lançamento feito fora da ficha (ex.: "Novo
 * afastamento" na lista de afastamentos). Desligado aparece, mas não é escolhível —
 * o backend recusaria o lançamento.
 */
export default function EmployeePickerModal({ token, title, onClose, onPick }: Props) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<DpEmployeeListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // A busca espera o usuário parar de digitar.
  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await listEmployeesApi(token, { q: search.trim() || undefined, limit: 20 });
        setItems(result.items);
      } catch (error: any) {
        toast.error(error?.message || "Erro ao buscar colaboradores.");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [token, search]);

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">{title}</h2>
          <button className="dpModal__close" onClick={onClose}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <div className="dpFormGroup">
            <label htmlFor="pickerSearch">Colaborador</label>
            <input
              id="pickerSearch"
              className="dpInput"
              placeholder="Buscar por nome ou CPF"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="dpTableWrapper">
            <table className="dpTable">
              <tbody>
                {loading ? (
                  <tr>
                    <td className="dpEmpty">Carregando...</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td className="dpEmpty">Nenhum colaborador encontrado.</td>
                  </tr>
                ) : (
                  items.map((employee) => {
                    const disabled = employee.status === "DESLIGADO";
                    return (
                      <tr
                        key={employee.id}
                        style={disabled ? { opacity: 0.5 } : { cursor: "pointer" }}
                        onClick={disabled ? undefined : () => onPick(employee)}
                        title={disabled ? "Colaborador desligado" : "Escolher"}
                      >
                        <td>
                          <strong>{employee.socialName || employee.fullName}</strong>
                          <div className="dpMuted">CPF {formatCpf(employee.cpf)}</div>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <EmployeeStatusBadge status={employee.status} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose}>
            Cancelar
          </button>
        </footer>
      </div>
    </div>
  );
}
